import React from 'react'
import { useFormikContext } from 'formik'
import {
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material'
import StraightenIcon from '@mui/icons-material/Straighten'
import HeightIcon from '@mui/icons-material/Height'
import NaturePeopleIcon from '@mui/icons-material/NaturePeople'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import ItemsContainer from '../../ItemsContainer'
import ToggleField from '../components/ToggleField'
import SliderField from '../components/SliderField'

const pickItems = (items, names) =>
  names.map(name => items.find(i => i.name === name)).filter(Boolean)

const SectionHeader = ({ icon: Icon, label }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
    <Typography variant="subtitle2" color="primary" fontWeight={600}>
      {label}
    </Typography>
  </Stack>
)

const DAP_CAP_TOGGLE = [
  { value: 'dap', label: 'DAP' },
  { value: 'cap', label: 'CAP' },
]

const hasValue = v => v !== '' && v !== null && v !== undefined

const DasometricStep = ({ itemsAttributes }) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext()
  const [dapCapType, setDapCapType] = React.useState('dap')

  const fieldError = name => (touched[name] && errors[name]) || ''
  const hasError = name => Boolean(touched[name] && errors[name])

  const rectitudItems = pickItems(itemsAttributes, ['fusteRectitud'])
  const espaciamientoItems = pickItems(itemsAttributes, ['espaciamiento'])

  const handleDapCapValue = val => {
    if (dapCapType === 'dap') {
      setFieldValue('dap', val)
      // Auto-calculate CAP = DAP * π
      const numVal = parseFloat(val)
      if (!isNaN(numVal) && numVal > 0) {
        setFieldValue('cap', (numVal * Math.PI).toFixed(2))
      }
    } else {
      setFieldValue('cap', val)
      // Auto-calculate DAP = CAP / π
      const numVal = parseFloat(val)
      if (!isNaN(numVal) && numVal > 0) {
        setFieldValue('dap', (numVal / Math.PI).toFixed(2))
      }
    }
  }

  const handleToggleChange = newType => {
    setDapCapType(newType)
  }

  const currentDapCapValue = dapCapType === 'dap' ? values.dap : values.cap

  return (
    <Stack spacing={2.5}>
      {/* DAP / CAP */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={RotateLeftIcon} label="Diámetro / Circunferencia" />
          <ToggleField
            label="Medición del fuste principal"
            value={currentDapCapValue || ''}
            onChange={handleDapCapValue}
            toggleOptions={DAP_CAP_TOGGLE}
            selectedToggle={dapCapType}
            onToggleChange={handleToggleChange}
            placeholder={dapCapType === 'dap' ? '45.2' : '142.0'}
            unit="cm"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {dapCapType === 'dap'
              ? `CAP calculado: ${values.cap || '—'} cm`
              : `DAP calculado: ${values.dap || '—'} cm`}
          </Typography>
        </CardContent>
      </Card>

      {/* Heights */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={HeightIcon} label="Alturas del fuste principal" />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputMode="decimal"
                name="ht"
                label="Altura Total"
                placeholder="15.0"
                value={values.ht || ''}
                onChange={e => setFieldValue('ht', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                  sx: { height: 48 },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputMode="decimal"
                name="hc"
                label="Altura Comercial"
                placeholder="8.0"
                value={values.hc || ''}
                onChange={e => setFieldValue('hc', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                  sx: { height: 48 },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bifurcations */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={NaturePeopleIcon} label="Bifurcaciones" />
          <TextField
            fullWidth
            required
            size="small"
            type="number"
            name="bifurcaciones"
            label="Número de bifurcaciones"
            placeholder="0"
            value={hasValue(values.bifurcaciones) ? values.bifurcaciones : ''}
            onChange={e => setFieldValue('bifurcaciones', e.target.value)}
            onBlur={() => setFieldTouched('bifurcaciones', true)}
            error={hasError('bifurcaciones')}
            helperText={fieldError('bifurcaciones')}
            inputProps={{ min: 0, max: 10, step: 1, inputMode: 'numeric' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">nro</InputAdornment>,
              sx: { height: 48 },
            }}
          />
        </CardContent>
      </Card>

      {/* Trunk Inclination */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={StraightenIcon} label="Inclinación del fuste principal" />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                inputMode="decimal"
                name="inclArbol"
                label="Grados"
                placeholder="0"
                value={hasValue(values.inclArbol) ? values.inclArbol : ''}
                onChange={e => setFieldValue('inclArbol', e.target.value)}
                onBlur={() => setFieldTouched('inclArbol', true)}
                error={hasError('inclArbol')}
                helperText={fieldError('inclArbol')}
                inputProps={{ min: 0, step: 'any' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">°</InputAdornment>,
                  sx: { height: 48 },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                inputMode="decimal"
                name="compInclArbol"
                label="Compensación"
                placeholder="0"
                value={hasValue(values.compInclArbol) ? values.compInclArbol : ''}
                onChange={e => setFieldValue('compInclArbol', e.target.value)}
                onBlur={() => setFieldTouched('compInclArbol', true)}
                error={hasError('compInclArbol')}
                helperText={fieldError('compInclArbol')}
                inputProps={{ min: 0, step: 'any' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                  sx: { height: 48 },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Crown */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={NaturePeopleIcon} label="Copa" />
          <Stack spacing={2} sx={{ px: 1 }}>
            <SliderField
              name="copaNS"
              label="Copa Norte-Sur"
              min={0}
              max={50}
              step={0.5}
              value={Number(values.copaNS) || 0}
              onChange={(name, val) => setFieldValue(name, val)}
              icon={NaturePeopleIcon}
              unit=" m"
            />
            <SliderField
              name="copaEW"
              label="Copa Este-Oeste"
              min={0}
              max={50}
              step={0.5}
              value={Number(values.copaEW) || 0}
              onChange={(name, val) => setFieldValue(name, val)}
              icon={NaturePeopleIcon}
              unit=" m"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Trunk Shape & Spacing */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <SectionHeader icon={StraightenIcon} label="Características del Fuste" />
          <Grid container spacing={2}>
            {rectitudItems.map(item => (
              <ItemsContainer key={item.name} {...item} x={12} s={6} m={6} editable />
            ))}
            {espaciamientoItems.map(item => (
              <ItemsContainer key={item.name} {...item} x={12} s={6} m={6} editable />
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  )
}

export default DasometricStep
